/**
 * 公共 API 客户端（L1-only）
 * baseURL 在请求拦截器中按当前页面协议动态计算，避免 Mixed Content
 */

import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { useUserStore } from '@/stores'
import { getCorrectedApiBaseUrl } from './api-base'
import { isL1Error, parseL1Error, toL1Error } from './l1-error'
import { maybePresentError, type MaybePresentErrorConfig, isAuthRoutePath } from './l1-error-presenter'
import { isL1Envelope, unwrapL1Response } from './l1-unwrap'

const request: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 600000,
  headers: {
    'Content-Type': 'application/json',
  },
})

function getPresentConfig(
  config?: InternalAxiosRequestConfig,
): MaybePresentErrorConfig | undefined {
  if (!config) {
    return undefined
  }
  return {
    skipErrorToast: config.skipErrorToast,
    errorBatchId: config.errorBatchId,
    errorI18nKey: config.errorI18nKey,
    requestUrl: config.url,
    requestMethod: config.method,
  }
}

/** 非登录页 4010：清 token 并跳转登录 */
function handleSessionExpired(l1Err: ReturnType<typeof toL1Error>): void {
  if (l1Err.code !== 4010) {
    return
  }
  if (typeof window === 'undefined' || isAuthRoutePath(window.location.pathname)) {
    return
  }
  const userStore = useUserStore()
  userStore.clearToken()
  window.location.href = '/login'
}

function rejectWithPresentedError(
  err: unknown,
  config?: InternalAxiosRequestConfig,
): Promise<never> {
  const l1Err = isL1Error(err) ? err : toL1Error(err)
  maybePresentError(l1Err, getPresentConfig(config))
  handleSessionExpired(l1Err)
  return Promise.reject(l1Err)
}

request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      config.baseURL = '/api/v1'
    } else {
      config.baseURL = getCorrectedApiBaseUrl()
    }
    const userStore = useUserStore()
    if (userStore.token && !config.skipAuth) {
      config.headers.set('Authorization', `Bearer ${userStore.token}`)
    }
    return config
  },
  (error) => Promise.reject(error),
)

request.interceptors.response.use(
  (response: AxiosResponse) => {
    if (import.meta.env.DEV) {
      console.log('📥 API响应成功:', {
        url: response.config.url,
        method: response.config.method?.toUpperCase(),
        status: response.status,
      })
    }

    if (response.config.responseType === 'blob') {
      return response
    }

    const data = response.data
    if (response.config.l1Exempt) {
      return data
    }

    if (isL1Envelope(data)) {
      try {
        return unwrapL1Response(data)
      } catch (err) {
        return rejectWithPresentedError(err, response.config)
      }
    }

    return data
  },
  (error) => {
    const { response, config } = error

    if (response) {
      const status = response.status ?? 0
      const data = response.data
      const l1Err = isL1Envelope(data)
        ? parseL1Error(data, status)
        : toL1Error(error, status)
      maybePresentError(l1Err, getPresentConfig(config))
      handleSessionExpired(l1Err)
      return Promise.reject(l1Err)
    }

    if (import.meta.env.DEV && error.code === 'ECONNABORTED') {
      console.error('⏱️ 请求超时:', config?.url)
    }

    const l1Err = toL1Error(error, 0)
    maybePresentError(l1Err, getPresentConfig(config))
    return Promise.reject(l1Err)
  },
)

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    /** 为 true 时不自动 toast */
    skipErrorToast?: boolean
    /** 并行批处理 ID，聚合为一条 toast */
    errorBatchId?: string
    /** 显式 i18n 键 */
    errorI18nKey?: string
    /** 非 L1 JSON 白名单，直通 raw body */
    l1Exempt?: boolean
    /** 为 true 时不附带 Authorization（公开观众 API） */
    skipAuth?: boolean
  }

  interface AxiosInstance {
    get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
    post<T = unknown>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): Promise<T>
    put<T = unknown>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): Promise<T>
    delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
    patch<T = unknown>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): Promise<T>
  }
}

export { request }
export default request
